import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../components/Modal';
import TripsGrid from '../components/TripsGrid';
import { VaporizeModal } from '../components/AssetActions';
import {
  useTrips,
  useAllFootage,
  createTrip,
  isTrashed,
  purgeDaysLeft,
  restoreTrip,
  vaporizeTrip,
  TRASH_RETENTION_DAYS,
} from '../data/firestore';
import { useAuth } from '../AuthContext';

function NewTripModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '',
    location: '',
    startDate: '',
    endDate: '',
    description: '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const id = await createTrip(form);
      onCreated?.(id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New trip" onClose={onClose}>
      <div className="cs-field">
        <label>Trip name</label>
        <input className="cs-input" value={form.name} onChange={set('name')} placeholder="e.g. Mentawai 2026" />
      </div>
      <div className="cs-field">
        <label>Location</label>
        <input className="cs-input" value={form.location} onChange={set('location')} />
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <div className="cs-field" style={{ flex: 1 }}>
          <label>Start date</label>
          <input className="cs-input" type="date" value={form.startDate} onChange={set('startDate')} />
        </div>
        <div className="cs-field" style={{ flex: 1 }}>
          <label>End date</label>
          <input className="cs-input" type="date" value={form.endDate} onChange={set('endDate')} />
        </div>
      </div>
      <div className="cs-field">
        <label>Description</label>
        <input className="cs-input" value={form.description} onChange={set('description')} />
      </div>
      <button className="cs-btn cs-btn-primary" disabled={busy || !form.name} onClick={save}>
        {busy ? 'Creating…' : 'Create trip'}
      </button>
    </Modal>
  );
}

export default function Trips() {
  const { user } = useAuth();
  const { docs: trips } = useTrips();
  const { docs: footage } = useAllFootage();
  const [adding, setAdding] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [vaporizeTarget, setVaporizeTarget] = useState(null);
  const navigate = useNavigate();

  const trashedTrips = useMemo(() => (trips || []).filter(isTrashed), [trips]);

  return (
    <div className="cs-page">
      <div className="cs-page-head">
        <h1>Trips</h1>
        {user && (
          <div style={{ display: 'flex', gap: 10 }}>
            {trashedTrips.length > 0 && (
              <button
                className={`cs-btn ${showTrash ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                onClick={() => setShowTrash((s) => !s)}
              >
                🗑 Trash ({trashedTrips.length})
              </button>
            )}
            <button className="cs-btn cs-btn-primary" onClick={() => setAdding(true)}>
              + New trip
            </button>
          </div>
        )}
      </div>

      {trips === null ? (
        <div>Loading…</div>
      ) : (
        <TripsGrid title="" trips={trips} assets={footage || []} />
      )}

      {user && showTrash && trashedTrips.length > 0 && (
        <section style={{ marginTop: 32 }}>
          <h2 style={{ fontSize: 18, color: 'var(--muted)' }}>
            🗑 Trash — recoverable for {TRASH_RETENTION_DAYS} days
          </h2>
          <div className="cs-trash-list">
            {trashedTrips.map((t) => (
              <div key={t.id} className="cs-trash-row">
                <div>
                  <div style={{ fontWeight: 600 }}>{t.name}</div>
                  <div className="meta">deletes in {purgeDaysLeft(t)} days · {t.assetCount || 0} files</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => restoreTrip(t.id)}>
                    Restore
                  </button>
                  <button className="cs-btn cs-btn-danger cs-btn-sm" onClick={() => setVaporizeTarget(t)}>
                    Vaporize
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {vaporizeTarget && (
        <VaporizeModal
          what={`the trip “${vaporizeTarget.name}” and ALL of its media`}
          onConfirm={() => vaporizeTrip(vaporizeTarget)}
          onClose={() => setVaporizeTarget(null)}
        />
      )}

      {adding && (
        <NewTripModal
          onClose={() => setAdding(false)}
          onCreated={(id) => {
            setAdding(false);
            navigate(`/trips/${id}`);
          }}
        />
      )}
    </div>
  );
}
